import {
  ArabicConjugation,
  ArabicConjugationType,
  ArabicLemma,
  Article,
  DanishLemma
} from "@/components/article/arabic/article";
import type {
  DanishArabicRow,
  DanishArabicRows
} from "@/components/article/arabic/response.interface";

export function parseRows(rowsList: DanishArabicRows[]): Article[] {
  const articles = [];

  for (const rows of rowsList) {
    if (rows.data.length === 0) {
      continue;
    }

    let danishId = null;
    let arabicId = null;

    let article = new Article();
    for (const row of rows.data) {
      if (danishId && arabicId && (row.danishId !== danishId || row.arabicId !== arabicId)) {
        articles.push(article);
        article = new Article();
      }

      if (row.danishId !== danishId) {
        article.lemma.danish = createDanishLemma(row);
        article.explanations.addDanish(row.referenceCategory);
        article.explanations.addDanish(row.danishAbbreviation, "forkortelse for ");
        article.explanations.addDanish(row.danishExplanation);
      }

      if (row.arabicId !== arabicId) {
        article.lemma.arabic = createArabicLemma(row);
        article.explanations.addArabic(row.arabicAbbreviation, "forkortelse for ");
        article.explanations.addArabic(row.arabicExplanation);
      }

      if (row.danishExample || row.arabicExample) {
        article.examples.push({
          danish: row.danishExample,
          arabic: row.arabicExample
        });
      }

      danishId = row.danishId;
      arabicId = row.arabicId;
    }

    articles.push(article);
  }

  return articles;
}

function createDanishLemma(row: DanishArabicRow): DanishLemma {
  return {
    word: row.danishWord,
    alternative: row.danishAlso,
    preposition: row.danishPreposition,

    conjugations: [
      [row.danishGender, row.danishPlural].filter(String),
      [row.danishPerfectTense, row.danishPastTense, row.danishPastPerfect].filter(String)
    ].filter((c) => c.length > 0),

    wordclass: {
      short: row.danishWordclass,
      latin: row.danishWordclassLatin,
      danish: row.danishWordclassDanish
    }
  };
}

function createArabicLemma(row: DanishArabicRow): ArabicLemma {
  return {
    word: row.arabicWord,
    pronunciation: row.arabicPronunciation,
    preposition: row.arabicPreposition,

    root: row.arabicRoot,
    form: row.arabicForm,

    conjugations: [
      new ArabicConjugation(row.arabicPlural, ArabicConjugationType.Pluralis),
      new ArabicConjugation(row.arabicFeminine, ArabicConjugationType.Femininum),
      new ArabicConjugation(row.arabicPastTense, ArabicConjugationType.Imperfektum),
      new ArabicConjugation(row.arabicDefinite, ArabicConjugationType.Definit)
    ].filter((c) => c.text),

    wordclass: {
      short: row.arabicWordclass,
      latin: row.arabicWordclassLatin,
      danish: row.arabicWordclassDanish
    }
  };
}
